import React, {
  useMemo,
  Dispatch,
  SetStateAction,
  useContext,
  useCallback,
} from 'react';
import { DealRoom } from 'models/DealRoom';
import DealContext from './Deal.context';
import ListingContext from '../Listing/Listing.context';
import useShowGetBuyers from 'hooks/useShowGetBuyers';
import useColleagues from 'hooks/useColleagues';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import { LeadStages } from 'models/Lead';
import api from 'api';
import { sortDealTopNodes } from 'utils';
import { isDataRoomAdminOrOwner } from 'router/subrouters/DataRoom/utils';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';

type DealProviderProps = {
  dealRoom: DealRoom;
  setDealRoom: Dispatch<SetStateAction<DealRoom>>;
  openDealStatusDrawer: () => void;
  openTransferDrawer: () => void;
  openManageListView: () => void;
};

const DealProvider: React.FC<DealProviderProps> = ({
  children,
  dealRoom,
  setDealRoom,
  openDealStatusDrawer,
  openTransferDrawer,
  openManageListView,
}) => {
  const dealRoomId = dealRoom._id;

  const { isLoading, colleagues, setColleagues, onNewOwner } =
    useColleagues(dealRoomId);

  const { isSuperAdmin } = useContext(CurrentUserContext);
  const {
    listing: { isOwner },
  } = useContext(ListingContext);

  const leadStagesCopies = useLeadStagesCopy().getCopies(
    dealRoom.leadStagesCopies,
  );

  const getShowGetBuyers = useShowGetBuyers();

  const showGetBuyers = useMemo(
    () => getShowGetBuyers(dealRoom),
    [dealRoom, getShowGetBuyers],
  );

  const dealRoomTopNodes = useMemo(
    () => sortDealTopNodes(dealRoom?.dataRoomTopNodes),
    [dealRoom?.dataRoomTopNodes],
  );

  const dealRoomStarredNodes = useMemo(
    () => sortDealTopNodes(dealRoom?.dataRoomStarredNodes),
    [dealRoom?.dataRoomStarredNodes],
  );

  const isDataRoomAdmin = useMemo(
    () => isSuperAdmin || isDataRoomAdminOrOwner(dealRoom?.role),
    [isSuperAdmin, dealRoom?.role],
  );

  const updateLeadStageCopy = useCallback(
    (stage: LeadStages, newValue: string) => {
      setDealRoom((prev) => {
        const updatedLeadStagesCopies = {
          ...prev.leadStagesCopies,
          [stage]: newValue,
        };

        api.dealRoom.updateDealRoom(dealRoom?._id, {
          leadStagesCopies: updatedLeadStagesCopies,
        });

        return {
          ...prev,
          leadStagesCopies: updatedLeadStagesCopies,
        };
      });
    },
    [dealRoom?._id, setDealRoom],
  );

  return (
    <DealContext.Provider
      value={{
        dealRoom,
        colleagues,
        areColleaguesLoading: isLoading,
        isOwner,
        showGetBuyers,
        dealRoomTopNodes,
        leadStagesCopies,
        dealRoomStarredNodes,
        isDataRoomAdmin,
        setDealRoom,
        setColleagues,
        onNewOwner,
        openDealStatusDrawer,
        openTransferDrawer,
        openManageListView,
        updateLeadStageCopy,
      }}
    >
      {children}
    </DealContext.Provider>
  );
};
export default DealProvider;
